import type {SelectOptionItem} from '@yandex-delivery/blocks'
import {Button, Input, Loader, Modal, Typography} from '@yandex-delivery/blocks'
import {useI18n} from '@yandex-delivery/core/i18n'
import {useDebounce} from '@yandex-delivery/hooks'
import {noop} from '@yandex-delivery/utils'
import clsx from 'clsx'
import {useCallback, useMemo, useState} from 'react'

import type {LocationDetectedVariant} from '../../types'
import {classes} from './SelectCityModal.classes'
import type {Props} from './types'
import {useFetchDetectLocation} from './use-fetch-detect-location'

export function SelectCityModal(props: Props) {
  const {open, onClose, selectedPointCity, onSelect = noop} = props
  const [customLocation, setCustomLocation] = useState<string>(selectedPointCity)
  const [selectedItem, setSelectedItem] = useState<SelectOptionItem<LocationDetectedVariant>>()
  const {fetchDetectLocationHandler, locations, isDetectLocation} = useFetchDetectLocation()
  const i18n = useI18n()

  const suggestedLocations = useMemo<SelectOptionItem<LocationDetectedVariant>[]>(() => {
    if (locations === undefined) {
      return []
    }

    return locations.map<SelectOptionItem<LocationDetectedVariant>>(item => ({
      label: item.address,
      value: item,
    }))
  }, [locations])

  const selectItemHandler = useCallback(
    (item: SelectOptionItem<LocationDetectedVariant>) => () => {
      setSelectedItem(prevState => (prevState?.value?.geo_id === item.value?.geo_id ? undefined : item))
    },
    [],
  )

  const confirmLocationHandler = useCallback((location?: string) => {
    setCustomLocation(location ?? '')
  }, [])

  const applyNewLocationHandler = useCallback(() => {
    onSelect(selectedItem?.value)
  }, [onSelect, selectedItem])

  useDebounce(
    () => {
      fetchDetectLocationHandler(customLocation)
    },
    500,
    [customLocation, fetchDetectLocationHandler],
  )

  return (
    <Modal className={classes.root} onClose={onClose} opened={open} size="m">
      <Modal.Header className={classes.header} title={i18n('ndd.pickup_points.change_city', 'Изменить город')}>
        <Input
          className={classes.cityInput}
          onChange={confirmLocationHandler}
          placeholder={i18n('ndd.pickup_points.change_city.placeholder', 'Введите город или регион')}
          trail={isDetectLocation && <Loader />}
          value={customLocation}
        />
      </Modal.Header>
      <Modal.Body className={classes.body}>
        <div className={classes.listRoot}>
          {suggestedLocations.map(item => (
            <div
              key={item.value?.address}
              className={clsx(classes.listItem, {
                [classes.selectedItem]: item.value?.geo_id === selectedItem?.value?.geo_id,
              })}
              onClick={selectItemHandler(item)}
            >
              <Typography variant="body2">{item?.label}</Typography>
            </div>
          ))}
        </div>
      </Modal.Body>

      <Modal.Footer className={classes.footer}>
        <Modal.Footer.Controls>
          <Button onPress={onClose}>{i18n('ndd.pickup_points.change_city.cancel', 'Отменить')}</Button>
          <Button disabled={!selectedItem} onPress={applyNewLocationHandler} variant="action">
            {i18n('ndd.pickup_points.change_city.next', 'Далее')}
          </Button>
        </Modal.Footer.Controls>
      </Modal.Footer>
    </Modal>
  )
}
